import {
  faChevronDown,
  faChevronRight,
} from "@fortawesome/pro-regular-svg-icons";
import { styled } from "@mui/material/styles";
import { RichTreeViewPro } from "@mui/x-tree-view-pro/RichTreeViewPro";
import { useCallback, useMemo, useState } from "react";

import { File } from "@/components/graphql/schema.graphql";
import { Icon } from "@/components/ui-v2";
import { CheckerboardBackground } from "@/components/ui-v2/Backgrounds/Checkerboard";
import {
  getTreeViewFromRasterLayers,
  NodeMetadata,
  RasterLayerTreeItem,
  ROOT_ITEM_ID,
} from "@/utils/rasterLayers";

import {
  GalleryLayerLegendItem,
  GalleryLayerLegendItemProps,
} from "./GalleryLayerLegendItem";

const ExpandIcon = () => <Icon icon={faChevronRight} />;
const CollapseIcon = () => <Icon icon={faChevronDown} />;

const LegendContainer = styled("div")(({ theme }) => ({
  position: "absolute",
  top: 40,
  right: 8,
  zIndex: 10,
  backgroundColor: theme.palette.secondary[6],
  boxShadow: `inset 0 0 0 0.5px ${theme.palette.secondary[4]}`,
  borderRadius: theme.spacing(1),
  padding: "4px",
  width: "200px",
  maxHeight: "calc(100% - 48px)",
  overflowY: "auto",
}));

interface RenderImageLayersProps {
  frame: [number, number];
  layers: RasterLayerTreeItem[];
}

const RenderImageLayers = (props: RenderImageLayersProps) => {
  const { frame, layers } = props;
  const [width, height] = frame;

  return (
    <>
      {layers.toReversed().map((layer) => {
        if (!layer.metadata.visible) {
          return null;
        }
        const leftPercent = (layer.metadata.left || 0) / width;
        const topPercent = (layer.metadata.top || 0) / height;
        const layerWidthPercent = (layer.metadata.width || width) / width;
        const layerHeightPercent = (layer.metadata.height || height) / height;
        return (
          <>
            <img
              src={layer.metadata.attachmentUrl}
              draggable={false}
              alt=""
              style={{
                position: "absolute",
                display: "block",
                left: `${leftPercent * 100}%`,
                top: `${topPercent * 100}%`,
                width: `${layerWidthPercent * 100}%`,
                height: `${layerHeightPercent * 100}%`,
                pointerEvents: "none",
              }}
            />
            {layer.children && (
              <RenderImageLayers frame={frame} layers={layer.children} />
            )}
          </>
        );
      })}
    </>
  );
};

interface GalleryImagePreviewLayersProps {
  fileMetadata?: File["metadata"];
  attachmentUrls?: File["attachmentUrls"];
  aspectRatio: string;
}

export const GalleryImagePreviewLayers = (
  props: GalleryImagePreviewLayersProps,
) => {
  const { fileMetadata, attachmentUrls, aspectRatio } = props;

  const treeView = useMemo(
    () =>
      getTreeViewFromRasterLayers(
        fileMetadata?.rasterLayers ?? [],
        attachmentUrls ?? {},
      ),
    [fileMetadata, attachmentUrls],
  );
  const [localTreeView, setLocalTreeView] = useState(treeView);
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [expandedItemIds, setExpandedItemIds] = useState<string[]>(
    localTreeView.items
      .filter((x) => x.metadata.kind === "group")
      .map((x) => x.id),
  );

  const setVisible = useCallback((nodeId: string, visible: boolean) => {
    setLocalTreeView((prevTreeView) => {
      const newNodeMap = { ...prevTreeView.nodeMap };
      let currentNodeId: string | null = nodeId;
      while (currentNodeId !== null && currentNodeId !== ROOT_ITEM_ID) {
        const currentNode: NodeMetadata | undefined = newNodeMap[currentNodeId];
        if (!currentNode) break;
        newNodeMap[currentNodeId] = {
          ...currentNode,
          visible,
        };
        if (!visible) {
          // if hiding, no need to continue to parents
          break;
        }
        currentNodeId = currentNode.parentId;
      }
      const newItems = prevTreeView.items.map((item) => ({
        ...item,
        metadata: newNodeMap[item.id],
      }));
      return {
        ...prevTreeView,
        items: newItems,
        nodeMap: newNodeMap,
      };
    });
  }, []);

  if (!fileMetadata?.width || !fileMetadata?.height) {
    return null;
  }
  const frame: [number, number] = [fileMetadata.width, fileMetadata.height];

  return (
    <>
      <CheckerboardBackground
        sx={{
          position: "absolute",
          inset: 0,
          margin: "auto",
          aspectRatio,
          maxWidth: "100%",
          maxHeight: "100%",
          overflow: "hidden",
        }}
      >
        <RenderImageLayers frame={frame} layers={localTreeView.items} />
      </CheckerboardBackground>
      <LegendContainer>
        <RichTreeViewPro
          expandedItems={expandedItemIds}
          onExpandedItemsChange={(e, itemIds) => {
            setExpandedItemIds(itemIds);
          }}
          // TODO: Here the typing fails in strict mode, but not in non-strict mode, fix it!
          // @ts-ignore - MUI typing is not correct
          selectedItems={selectedItemIds}
          onSelectedItemsChange={(e, itemIds) => {
            // @ts-ignore - MUI typing is not correct
            setSelectedItemIds(itemIds);
          }}
          slots={{
            expandIcon: ExpandIcon,
            collapseIcon: CollapseIcon,
            item: GalleryLayerLegendItem as typeof GalleryLayerLegendItem &
              React.JSXElementConstructor<unknown>,
          }}
          slotProps={{
            item: {
              nodeMap: localTreeView.nodeMap,
              setVisible,
            } as GalleryLayerLegendItemProps,
          }}
          items={localTreeView.items}
        />
      </LegendContainer>
    </>
  );
};
